import {
  sendVaultToNewDevice,
  parseDeviceLinkSessionId,
  parseDeviceLinkQr,
} from '../../deviceLink/deviceLinkService.ts'
import {
  isLiveQrScannerLikelySupported,
  startLiveQrScanner,
} from '../../deviceLink/deviceLinkScanner.ts'
import { t } from '../../i18n/index.ts'
import { appendBlocks } from '../dom.ts'
import { getParam, navigate } from '../router.ts'

export async function renderLinkDeviceScanScreen(root: HTMLElement): Promise<void> {
  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="topbar premium-topbar">
        <button type="button" class="icon-btn" id="btn-back">←</button>
        <h2 class="topbar-title">${t('link_device_title')}</h2>
      </header>
      <section class="card premium-card">
        <p class="status muted small">${t('link_device_hint')}</p>
        <div id="scan-video-wrap" class="qr-wrap scan-live" hidden style="display:none">
          <video id="scan-video" playsinline muted></video>
        </div>
        <p id="scan-status" class="status muted cyber-status">${t('link_device_status_ready')}</p>
        <label class="file-label">
          ${t('link_device_upload_label')}
          <input type="file" id="scan-file" accept="image/*" capture="environment" hidden />
        </label>
        <button type="button" id="btn-pick-qr" class="primary">${t('link_device_btn_camera')}</button>
        <textarea id="scan-paste" rows="3" placeholder="${t('link_device_placeholder')}" autocomplete="off" hidden></textarea>
        <button type="button" id="btn-send-vault" hidden>${t('link_device_btn_send_vault')}</button>
        <p id="scan-error" class="status err" hidden></p>
      </section>
    `,
  )

  const statusEl = root.querySelector('#scan-status') as HTMLElement
  const errEl = root.querySelector('#scan-error') as HTMLElement
  const pasteEl = root.querySelector('#scan-paste') as HTMLTextAreaElement
  const fileInput = root.querySelector('#scan-file') as HTMLInputElement
  const videoWrap = root.querySelector('#scan-video-wrap') as HTMLElement
  const videoEl = root.querySelector('#scan-video') as HTMLVideoElement

  let stopScanner: (() => void) | null = null
  let transferStarted = false

  const setStatus = (text: string, kind: 'muted' | 'ok' | 'err' = 'muted'): void => {
    statusEl.textContent = text
    statusEl.className = `status cyber-status ${kind}`
  }

  const isValidPayload = (raw: string): boolean =>
    Boolean(parseDeviceLinkSessionId(raw) || parseDeviceLinkQr(raw))

  const beginTransfer = async (raw: string): Promise<void> => {
    if (transferStarted) return
    const trimmed = raw.trim()
    if (!isValidPayload(trimmed)) {
      errEl.hidden = false
      errEl.textContent = t('link_device_err_invalid_qr')
      setStatus(t('link_device_status_invalid'), 'err')
      return
    }
    transferStarted = true
    stopScanner?.()
    stopScanner = null
    errEl.hidden = true
    pasteEl.value = trimmed
    try {
      await sendVaultToNewDevice(trimmed, setStatus)
      setStatus(t('link_device_status_sync_complete'), 'ok')
      window.setTimeout(() => navigate('settings'), 1200)
    } catch (err) {
      transferStarted = false
      setStatus(t('link_device_status_transfer_failed'), 'err')
      errEl.hidden = false
      errEl.textContent = String(err)
      void startScanner()
    }
  }

  const onQrDetected = (raw: string): void => {
    setStatus(t('link_device_status_qr_detected'))
    void beginTransfer(raw)
  }

  const showVideo = (visible: boolean): void => {
    videoWrap.hidden = !visible
    videoWrap.style.display = visible ? '' : 'none'
  }

  const startScanner = async (): Promise<void> => {
    stopScanner?.()
    showVideo(false)
    errEl.hidden = true

    if (!isLiveQrScannerLikelySupported()) {
      setStatus(t('link_device_status_tap_camera'))
      return
    }

    const stop = await startLiveQrScanner(videoEl, onQrDetected, () => {
      showVideo(false)
      setStatus(t('link_device_status_use_camera_below'))
    })
    if (stop) {
      stopScanner = stop
      showVideo(true)
      setStatus(t('link_device_status_point_camera'))
    } else {
      showVideo(false)
      setStatus(t('link_device_status_use_camera_below'))
    }
  }

  root.querySelector('#btn-back')!.addEventListener('click', () => {
    stopScanner?.()
    navigate('settings')
  })

  root.querySelector('#btn-pick-qr')!.addEventListener('click', () => fileInput.click())

  fileInput.addEventListener('change', () => {
    void (async () => {
      const file = fileInput.files?.[0]
      fileInput.value = ''
      if (!file) return
      setStatus(t('link_device_status_decoding'))
      try {
        const { default: jsQR } = await import('jsqr')
        const bitmap = await createImageBitmap(file)
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(bitmap, 0, 0)
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const found = jsQR(img.data, img.width, img.height)
        const raw = found?.data?.trim() ?? ''
        if (raw) {
          await beginTransfer(raw)
        } else {
          setStatus(t('link_device_status_qr_not_found'), 'err')
        }
      } catch (err) {
        setStatus(t('link_device_status_decode_failed'), 'err')
        errEl.hidden = false
        errEl.textContent = String(err)
      }
    })()
  })

  root.querySelector('#btn-send-vault')!.addEventListener('click', () => {
    void beginTransfer(pasteEl.value)
  })

  const autoSid = getParam('sessionId')?.trim()
  if (autoSid) {
    setStatus(t('link_device_status_opened_from_qr'))
    void beginTransfer(autoSid)
  } else {
    void startScanner()
  }
}
